import React, { useEffect } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../../redux/slices/auth";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputPassword from "../../components/InputPassword/InputPassword";
import "./LoginForm.css";

const LoginForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const dispatch = useDispatch();
  const { status, isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/"); // redirect after login
    }
  }, [isAuthenticated, navigate]);

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("Username is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    rememberMe: Yup.boolean(),
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
      rememberMe: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!executeRecaptcha) {
        toast.error("reCAPTCHA not ready");
        return;
      }

      const token = await executeRecaptcha("login_form");

      try {
        await dispatch(
          loginUser({
            username: values.username,
            password: values.password,
            token,
          })
        ).unwrap(); // <--- handles rejectWithValue cleanly

        // If we get here, login succeeded
        navigate("/");
      } catch (err) {
        // err will be what you returned from rejectWithValue()
        toast.error(err?.detail || "Email or password is incorrect.");
      }
    },
  });

  return (
    <section className="LoginForm">
      <div className="login-form-wrapper">
        <form onSubmit={formik.handleSubmit}>
          <h3>Log in to your Account</h3>
          <p>Welcome back! Log in to start streaming</p>

          {/* Username Field */}
          <div className="login-form-group">
            <input
              type="text"
              name="username"
              placeholder="Username"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.username}
              className={
                formik.touched.username && formik.errors.username ? "error" : ""
              }
            />
            {formik.touched.username && formik.errors.username && (
              <div className="error-message">
                <p>{formik.errors.username}</p>
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="login-form-group">
            <InputPassword
              name="password"
              placeholder="Password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              className={
                formik.touched.password && formik.errors.password ? "error" : ""
              }
            />
            {formik.touched.password && formik.errors.password && (
              <div className="error-message">
                <p>{formik.errors.password}</p>
              </div>
            )}
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="form-meta">
            <label>
              <input
                type="checkbox"
                name="rememberMe"
                checked={formik.values.rememberMe}
                onChange={formik.handleChange}
              />
              Remember me
            </label>
            <Link to="/forgot-password">Forgot password?</Link>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === "loading" || formik.isSubmitting}
          >
            {status === "loading" ? "Logging in..." : "Login"}
          </button>

          {/* Sign Up Link */}
          <p className="signup-link">
            Don't have an account? <Link to="/register">Create an account</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default LoginForm;
